import { createQueryKeys } from '@lukemorales/query-key-factory';
import * as Sentry from '@sentry/react';
import { TypeCompiler } from '@sinclair/typebox/compiler';
import { useQuery } from '@tanstack/react-query';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
  toJS,
} from 'mobx';
import pDebounce from 'p-debounce';
import * as React from 'react';
import type { WritableDeep } from 'type-fest';
import { getCvPartner } from 'Cvpartner';
import { toIso } from 'auto-converted/lib/CountryLanguageUtils';
import { type Role, User } from 'schema/user';
import { http, httpWarnChecked, queryClient } from 'util/fetch';
import { invariant } from 'util/invariant';
import { redirectTo } from 'util/routes';
import { I18n } from 'util/translations';
import { InterfaceSettingsProvider } from './InterfaceSetting';
import { prefetchLoggedInStatus, useIsLoggedIn } from './LoggedInStatus';

const checker = TypeCompiler.Compile(User);

const queries = createQueryKeys('current-user', {
  fetch: {
    queryKey: null,
    queryFn: ({ signal }) =>
      httpWarnChecked.get('/api/v1/currentuser', checker, { signal }),
  },
});

// TODO: avoid using `Partial` here
type CurrentUserType = Partial<User>;

class CurrentUserClass {
  // TODO: make `null` instead of `{}` by default
  current_user: WritableDeep<CurrentUserType> = {};
  private queued_user_updates: [
    user: CurrentUserType,
    callback: (() => void) | undefined,
  ][] = [];

  constructor() {
    makeObservable<
      CurrentUserClass,
      | 'queued_user_updates'
      | 'update'
      | 'update_debounced'
      | 'roles'
      | 'check_extra_role_permissions'
    >(this, {
      check_extra_role_permissions: false,
      hide_download: false,
      hide_preview: false,
      queued_user_updates: false,
      current_user: observable,
      reload: action,
      reload_if_not_loaded: action,
      toggle_expand_proposals: action,
      toggle_international_state: action,
      update_selected_office_ids: action,
      update_include_officeless_reference_projects: action,
      update_selected_tag_ids: action,
      update_preferred_download_format: action,
      update: false,
      update_debounced: false,
      update_country_and_office: action,
      is_loaded: computed,
      id: computed,
      name: computed,
      email: computed,
      company_id: computed,
      country_id: computed,
      office_id: computed,
      roles: computed,
      is_internationalmanager: computed,
      is_countrymanager: computed,
      is_departmentmanager: computed,
      is_reference_manager: computed,
      is_limited_access_user: computed,
      other_languages: computed,
      preferred_language: computed,
      masterdata_languages: computed,
      language_code: computed,
      language_codes: computed,
      ui_language_code: computed,
      ui_language_code_iso: computed,
      preferred_download_format: computed,
      country_code: computed,
      default_cv_id: computed,
      image: computed,
      international_toggle: computed,
    });
  }

  async reload() {
    const current_user = await queryClient.fetchQuery(queries.fetch);

    runInAction(() => {
      this.current_user = current_user as CurrentUserType;
    });
  }

  async reload_if_not_loaded() {
    if (this.is_loaded) {
      return;
    }

    await this.reload();
  }

  toggle_expand_proposals(new_state = false) {
    this.current_user.expand_proposals_toggle = new_state;
    return this.update({ expand_proposals_toggle: new_state });
  }

  toggle_international_state(state: 'expand' | 'hide') {
    if (this.current_user.international_toggle !== state) {
      this.current_user.international_toggle = state;
      return this.update({ international_toggle: state });
    }
  }

  update_selected_office_ids(new_selected_office_ids: readonly string[]) {
    const selected_office_ids = new_selected_office_ids as string[];
    this.current_user.selected_office_ids = selected_office_ids;
    return this.update({ selected_office_ids });
  }

  update_include_officeless_reference_projects(
    new_include_officeless_reference_projects: boolean,
  ) {
    this.current_user.include_officeless_reference_projects =
      new_include_officeless_reference_projects;
    return this.update({
      include_officeless_reference_projects:
        new_include_officeless_reference_projects,
    });
  }

  update_selected_tag_ids(new_selected_tag_ids: readonly string[]) {
    const selected_tag_ids = new_selected_tag_ids as string[];
    this.current_user.selected_tag_ids = selected_tag_ids;
    return this.update({ selected_tag_ids });
  }

  update_preferred_download_format(preferred_download_format: string) {
    this.current_user.preferred_download_format = preferred_download_format;
    return this.update({
      preferred_download_format,
    });
  }

  private async update(attributes: CurrentUserType, callback?: () => void) {
    queryClient.setQueryData(queries.fetch.queryKey, toJS(this.current_user));

    this.queued_user_updates.push([attributes, callback]);

    await this.update_debounced();
  }

  private update_debounced = pDebounce(async () => {
    const { user, callbacks } = this.queued_user_updates.reduce<{
      user: CurrentUserType;
      callbacks: (() => void)[];
    }>(
      (acc, [user, callback]) => {
        Object.assign(acc.user, user);
        if (callback) {
          acc.callbacks.push(callback);
        }

        return acc;
      },
      { user: {}, callbacks: [] },
    );

    // clear the queue
    this.queued_user_updates = [];

    const url = '/api/v1/currentuser';
    const current_user = await http.put<User>(url, { body: { user } });

    // only update cache if the update queue is empty
    if (this.queued_user_updates.length === 0) {
      runInAction(() => {
        this.current_user = current_user;
      });

      queryClient.setQueryData(queries.fetch.queryKey, current_user);
    }

    if (callbacks.length > 0) {
      await Promise.all(callbacks.map((callback) => callback()));
    }
  }, 500);

  update_country_and_office(
    attributes: CurrentUserType,
    redirect_to_path?: string,
  ) {
    const callback = () => {
      if (redirect_to_path) {
        return redirectTo(redirect_to_path);
      }
    };
    return this.update(attributes, callback);
  }

  get is_loaded() {
    return Object.keys(this.current_user).length > 0;
  }

  get id() {
    return this.current_user._id!;
  }

  get name() {
    return this.current_user.name;
  }

  get email() {
    return this.current_user.email;
  }

  get company_id() {
    return this.current_user.company_id;
  }

  get country_id() {
    return this.current_user.country_id;
  }

  get office_id() {
    return this.current_user.office_id;
  }

  private get roles(): readonly Role[] {
    return this.current_user.roles ?? [];
  }

  get other_languages(): readonly string[] {
    return this.masterdata_languages.slice(1);
  }

  get preferred_language() {
    return this.masterdata_languages[0] || 'int';
  }

  get masterdata_languages(): readonly string[] {
    return this.current_user.masterdata_languages ?? [];
  }

  get is_internationalmanager() {
    return this.roles.includes('internationalmanager');
  }

  get is_countrymanager() {
    if (this.is_internationalmanager) {
      return false;
    }

    return this.roles.includes('countrymanager');
  }

  get is_departmentmanager() {
    return this.roles.includes('departmentmanager');
  }

  get is_reference_manager() {
    return this.roles.includes('referencemanager');
  }

  get is_limited_access_user() {
    return this.roles.includes('limited_access');
  }

  get ui_language_code() {
    return this.current_user.ui_language_code || 'int';
  }

  get ui_language_code_iso() {
    return toIso(this.ui_language_code);
  }

  get language_code() {
    return this.current_user.language_code || 'int';
  }

  get language_codes() {
    return this.current_user.language_codes || [];
  }

  get preferred_download_format() {
    return this.current_user.preferred_download_format;
  }

  get country_code() {
    return this.current_user.country_code;
  }

  private check_extra_role_permissions(
    office_id: string,
    custom_tag_ids: readonly string[],
    country_id: string | null,
  ) {
    const allowed_office_ids = this.current_user.role_allowed_office_ids;

    // Department manager of office
    if (allowed_office_ids?.includes(office_id)) {
      return false;
    }

    const allowed_custom_tag_ids = this.current_user.role_allowed_tag_ids;

    // Department manager of custom tag
    if (
      allowed_custom_tag_ids?.some((value) => custom_tag_ids.includes(value))
    ) {
      return false;
    }

    // Country manager of country
    if (this.is_countrymanager && this.country_id == country_id) {
      return false;
    }

    return true;
  }

  hide_download(
    user_id: string,
    country_id: string | null,
    office_id: string,
    custom_tag_ids: readonly string[],
  ) {
    if (!this.is_limited_access_user || user_id == this.id) {
      return false;
    }

    return this.check_extra_role_permissions(
      office_id,
      custom_tag_ids,
      country_id,
    );
  }

  hide_preview(
    user_id: string,
    country_id: string | null,
    office_id: string,
    custom_tag_ids: readonly string[],
  ) {
    if (!this.is_limited_access_user || user_id == this.id) {
      return false;
    }

    return this.check_extra_role_permissions(
      office_id,
      custom_tag_ids,
      country_id,
    );
  }

  get default_cv_id() {
    return this.current_user?.default_cv_id ?? null;
  }

  get image() {
    return this.current_user?.image ?? null;
  }

  get international_toggle() {
    return this.current_user.international_toggle ?? 'hide';
  }
}

export const CurrentUser = new CurrentUserClass();

/**
 * Modify an array of roles into an array of roles that needs to be shown to users
 */
export function displayable_roles(roles: readonly Role[]): readonly Role[] {
  if (roles.length === 1) {
    return roles;
  }
  if (roles.includes('internationalmanager')) {
    return ['internationalmanager'];
  }
  return roles.filter(
    (role) => role !== 'consultant' && role !== 'internationalmanager',
  );
}

export function useCurrentUser() {
  const isLoggedIn = useIsLoggedIn();

  const { data } = useQuery({
    ...queries.fetch,
    staleTime: 60_000,
    enabled: isLoggedIn,
  });

  return { user: data, isLoggedIn };
}

export async function prefetchCurrentUser() {
  await Promise.all([
    queryClient.prefetchQuery({ ...queries.fetch, staleTime: Infinity }),
    prefetchLoggedInStatus(),
    CurrentUser.reload_if_not_loaded(),
  ]);
}

interface LanguageExtensions {
  readonly languageCode: string;
  readonly languageCodes: readonly string[];
  readonly uiLanguageCode: string;

  readonly languageCodeIso: string;
  readonly languageCodesIso: readonly string[];
  readonly uiLanguageCodeIso: string;

  readonly dateFormat: Intl.DateTimeFormat;

  readonly preferredLanguage: string;
  readonly firstLanguage: string | undefined;
  readonly otherLanguages: readonly string[];

  readonly preferredLanguageIso: string;
  readonly firstLanguageIso: string | undefined;
  readonly otherLanguagesIso: readonly string[];
}

function calculateLanguages(user: User): LanguageExtensions {
  const {
    language_code: languageCode,
    language_codes: languageCodes,
    ui_language_code: uiLanguageCode,
    masterdata_languages,
  } = user;

  const languageCodeIso = toIso(languageCode);
  const languageCodesIso = languageCodes.map(toIso);
  const uiLanguageCodeIso = toIso(uiLanguageCode);

  const dateFormat = new Intl.DateTimeFormat(uiLanguageCodeIso, {
    dateStyle: 'medium',
  });

  const [firstLanguage, ...otherLanguages] = masterdata_languages;
  const preferredLanguage = firstLanguage || 'int';

  const preferredLanguageIso = toIso(preferredLanguage);
  const firstLanguageIso = firstLanguage ? toIso(firstLanguage) : undefined;
  const otherLanguagesIso = otherLanguages.map(toIso);

  return {
    languageCode,
    languageCodes,
    uiLanguageCode,

    languageCodeIso,
    languageCodesIso,
    uiLanguageCodeIso,

    dateFormat,

    preferredLanguage,
    firstLanguage,
    otherLanguages,

    preferredLanguageIso,
    firstLanguageIso,
    otherLanguagesIso,
  };
}

interface RolesExtensions {
  readonly isInternationalmanager: boolean;
  readonly isCountrymanager: boolean;
  readonly isDepartmentmanager: boolean;
  readonly isReferenceManager: boolean;
  readonly isLimitedAccessUser: boolean;
  readonly isExternalUser: boolean;
  readonly isConsultant: boolean;

  readonly displayableRoles: readonly Role[];
  readonly displayableRolesUserNames: string;

  readonly grantableRoles: readonly Role[];
  readonly grantableApiUserRoles: readonly Role[];
}

function calculateRoles(user: User): RolesExtensions {
  const displayableRoles = displayable_roles(user.roles);

  const roles = new Set(user.roles);

  const isInternationalmanager = roles.has('internationalmanager');
  const isCountrymanager =
    !isInternationalmanager && roles.has('countrymanager');

  return {
    isInternationalmanager,
    isCountrymanager,
    isDepartmentmanager: roles.has('departmentmanager'),
    isReferenceManager: roles.has('referencemanager'),
    isLimitedAccessUser: roles.has('limited_access'),
    isExternalUser: roles.has('external'),
    isConsultant: roles.has('consultant'),

    displayableRoles,
    displayableRolesUserNames: displayableRoles
      .map((role) => I18n.t(`role_labels.${role}`))
      .join(', '),

    grantableRoles: isCountrymanager
      ? ['external', 'limited_access', 'consultant', 'countrymanager']
      : isInternationalmanager
        ? [
            'external',
            'limited_access',
            'consultant',
            'countrymanager',
            'referencemanager',
            'internationalmanager',
            'departmentmanager',
          ]
        : [],
    grantableApiUserRoles: (isCountrymanager
      ? (['consultant', 'countrymanager'] as const)
      : isInternationalmanager
        ? (['consultant', 'countrymanager', 'internationalmanager'] as const)
        : []
    )
      .map((role) => [`readonly_${role}`, role] as const)
      .flat(),
  };
}

interface ContextUser extends User, LanguageExtensions, RolesExtensions {
  readonly companyGroupId: string | undefined;
}

function useUpdateSentryUser(user: User | undefined) {
  const sentryUser = React.useMemo(() => {
    if (user == null) {
      return null;
    }

    return {
      id: user.id,
      email: user.email,
      name: user.name,
      roles: user.roles.join(','),
    };
  }, [user]);
  const userRef = React.useRef<typeof sentryUser>(null);

  React.useEffect(() => {
    const { faro } = getCvPartner();

    if (sentryUser == null) {
      Sentry.setUser(null);
      faro?.api.resetUser();
      return;
    }

    if (
      userRef.current != null &&
      userRef.current.id === sentryUser.id &&
      userRef.current.email === sentryUser.email &&
      userRef.current.name === sentryUser.name &&
      userRef.current.roles === sentryUser.roles
    ) {
      return;
    }

    userRef.current = sentryUser;

    const { roles, ...minimalUser } = sentryUser;
    Sentry.setUser(minimalUser);

    faro?.api.setUser({
      ...minimalUser,
      attributes: roles.length > 0 ? { roles } : undefined,
    });
  }, [sentryUser]);
}

const CurrentUserContext = React.createContext<ContextUser | null>(null);

export const UserProvider: React.VFC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isLoggedIn, user } = useCurrentUser();

  invariant(isLoggedIn !== false, 'User must be logged in');

  useUpdateSentryUser(user);

  const value = React.useMemo<ContextUser | null>(() => {
    if (user == null) {
      return null;
    }

    // We assume a company only belongs to one company group for now
    const companyGroupId = user.company_group_ids.at(0);

    return {
      ...user,

      companyGroupId,

      ...calculateLanguages(user),

      ...calculateRoles(user),
    };
  }, [user]);

  if (value == null) {
    return null;
  }

  return (
    <InterfaceSettingsProvider>
      <CurrentUserContext.Provider value={value}>
        {children}
      </CurrentUserContext.Provider>
    </InterfaceSettingsProvider>
  );
};

export function useLoggedInUser(): ContextUser {
  const user = React.useContext(CurrentUserContext);

  invariant(user, 'User must be available');

  return user;
}

export function useCompanyGroupId() {
  const user = useLoggedInUser();

  return user.companyGroupId;
}

export function useLanguages() {
  const {
    languageCode,
    languageCodes,
    uiLanguageCode,

    languageCodeIso,
    languageCodesIso,
    uiLanguageCodeIso,

    dateFormat,

    preferredLanguage,
    firstLanguage,
    otherLanguages,

    preferredLanguageIso,
    otherLanguagesIso,

    masterdata_languages,
  } = useLoggedInUser();

  return {
    languageCode,
    languageCodes,
    uiLanguageCode,

    languageCodeIso,
    languageCodesIso,
    uiLanguageCodeIso,

    dateFormat,

    preferredLanguage,
    firstLanguage,
    otherLanguages,

    preferredLanguageIso,
    otherLanguagesIso,

    masterdata_languages,
  };
}
